import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const dataElement = document.getElementById('lz-wardrobe-data');
const catalogue = JSON.parse(dataElement.textContent);
const { products, sets, labels } = catalogue;
const root = window.Shopify?.routes?.root || '/';
const dialog = document.getElementById('lz-wardrobe-upsell');
let activeOffer = null;
let mutationPending = false;
let offerSequence = 0;
const money = (cents) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(cents / 100);
const drawer = () => document.getElementById('cart-drawer');
const signature = (cart) => cart.items.map((item) => `${item.key}:${item.quantity}`).sort().join('|');
const productIsEligible = (product) => product?.available && product.id && product.price === 149900;
const node = (tag, className, content) => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (content !== undefined) element.textContent = content;
  return element;
};

async function getCart() {
  const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw new Error(labels.error);
  return response.json();
}

async function addProducts(handles) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const items = handles.map((handle) => ({ id: products[handle].id, quantity: 1 }));
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add', context: 'cart',
    lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
    promise: deferred.promise,
  }));
  try {
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId))].slice(0, 5);
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
    });
    const added = await response.json();
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, sections: response.ok ? added.sections : undefined, source: 'lz-wardrobe', didError: !response.ok },
    });
    if (!response.ok) throw new Error(added.description || labels.error);
    drawer()?.open();
    return cart;
  } catch (error) {
    deferred.resolve({ detail: { source: 'lz-wardrobe', didError: true } });
    throw error;
  }
}

function renderCards() {
  const container = document.querySelector('[data-lz-wardrobe-cards]');
  if (!container) return;
  container.querySelector('.lz-wardrobe__loading')?.remove();
  for (const size of [2, 3]) {
    const group = node('section', 'lz-wardrobe__group');
    group.id = size === 2 ? 'duos' : 'trios';
    group.setAttribute('aria-labelledby', `${group.id}-title`);
    const heading = node('div', 'lz-wardrobe__group-heading');
    const title = node('h2', '', size === 2 ? labels.duos : labels.trios);
    title.id = `${group.id}-title`;
    heading.append(title, node('p', '', size === 2 ? labels.duo_description : labels.trio_description));
    const grid = node('div', 'lz-wardrobe__grid');
    for (const set of sets.filter((entry) => entry.size === size)) {
      const card = node('article', 'lz-set');
      const pictures = node('div', 'lz-set__pictures');
      pictures.style.setProperty('--lz-set-size', size);
      const names = node('div', 'lz-set__names');
      for (const handle of set.handles) {
        const product = products[handle];
        const imageLink = node('a');
        imageLink.href = product.url;
        const image = node('img');
        image.src = product.image;
        image.alt = product.title;
        image.width = 500;
        image.height = 625;
        image.loading = 'lazy';
        imageLink.append(image);
        pictures.append(imageLink);
        const name = node('a', '', product.title);
        name.href = product.url;
        names.append(name);
      }
      const purchase = node('div', 'lz-set__purchase');
      const pricing = node('div');
      const original = set.handles.reduce((sum, handle) => sum + (products[handle].price || 0), 0);
      const price = size === 2 ? 269900 : 369900;
      pricing.append(node('span', 'lz-set__price', money(price)), node('span', 'lz-set__was', money(original)), node('span', 'lz-set__saving', `${labels.saved} ${money(original - price)}`));
      const button = node('button', 'lz-btn', size === 2 ? labels.add_set_duo : labels.add_set_trio);
      button.type = 'button';
      button.dataset.lzSet = set.id;
      button.setAttribute('aria-label', `${button.textContent}: ${set.name}`);
      if (!set.handles.every((handle) => productIsEligible(products[handle]))) {
        button.disabled = true;
        button.textContent = labels.unavailable;
      }
      purchase.append(pricing, button);
      const status = node('p', 'lz-wardrobe__status');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      card.append(pictures, names, node('h3', '', set.name), node('p', 'lz-set__mood', set.mood), node('p', 'lz-set__copy', set.copy), purchase, status);
      grid.append(card);
    }
    group.append(heading, grid);
    container.append(group);
  }
  if (window.location.hash === '#duos' || window.location.hash === '#trios') {
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
  }
}

function chooseOffer(cart) {
  if (cart.items.some((item) => !products[item.handle] || item.original_price !== 149900)) return null;
  const count = cart.items.reduce((sum, item) => sum + item.quantity, 0);
  if (count !== 1 && count !== 2) return null;
  const existing = cart.items.map((item) => item.handle);
  const candidates = sets.filter((set) => set.size === count + 1)
    .map((set) => ({ set, score: existing.filter((handle) => set.handles.includes(handle)).length }))
    .sort((a, b) => b.score - a.score);
  for (const { set } of candidates) {
    const handle = set.handles.find((entry) => !existing.includes(entry) && productIsEligible(products[entry]));
    if (handle) return { handle, size: count + 1, set, signature: signature(cart) };
  }
  return null;
}

function paintOffer(offer) {
  activeOffer = offer;
  const product = products[offer.handle];
  const isDuo = offer.size === 2;
  dialog.querySelector('#lz-upsell-title').textContent = isDuo ? labels.duo_title : labels.trio_title;
  dialog.querySelector('#lz-upsell-copy').textContent = isDuo ? labels.duo_copy : labels.trio_copy;
  const image = dialog.querySelector('[data-lz-upsell-image]');
  image.src = product.image;
  image.alt = product.title;
  dialog.querySelector('[data-lz-upsell-product]').href = product.url;
  dialog.querySelector('[data-lz-upsell-name]').textContent = product.title.toLowerCase();
  dialog.querySelector('[data-lz-upsell-reason]').textContent = offer.set.mood;
  dialog.querySelector('[data-lz-upsell-price]').textContent = `${isDuo ? labels.duos : labels.trios} · ${money(isDuo ? 269900 : 369900)}`;
  dialog.querySelector('[data-lz-upsell-saving]').textContent = `${labels.saved} ${money(isDuo ? 29900 : 79800)}`;
  dialog.querySelector('[data-lz-upsell-add]').textContent = isDuo ? labels.add_duo : labels.add_trio;
  dialog.querySelector('[data-lz-upsell-status]').textContent = '';
}

function closeOffer(showCart = true) {
  offerSequence++;
  if (dialog.open) dialog.close();
  activeOffer = null;
  if (showCart) drawer()?.open();
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add' || event.context !== 'product') return;
  const sequence = ++offerSequence;
  event.promise?.then(async ({ detail }) => {
    if (detail?.didError || detail?.source === 'lz-wardrobe') return;
    const cart = await getCart();
    const offer = chooseOffer(cart);
    if (!offer || sequence !== offerSequence) return;
    requestAnimationFrame(async () => {
      if (sequence !== offerSequence || document.querySelector('iframe[src*="fastrr"][style*="visible"]')) return;
      await drawer()?.close();
      if (sequence !== offerSequence) return;
      if (document.querySelector('dialog[open]')) return;
      paintOffer(offer);
      dialog.showModal();
    });
  }).catch(() => {});
});

dialog.addEventListener('cancel', (event) => {
  event.preventDefault();
  if (!mutationPending) closeOffer();
});
dialog.addEventListener('click', (event) => {
  if (event.target === dialog && !mutationPending) closeOffer();
});
dialog.querySelectorAll('[data-lz-upsell-close]').forEach((button) => button.addEventListener('click', () => {
  if (!mutationPending) closeOffer();
}));

document.addEventListener('click', async (event) => {
  const button = event.target.closest?.('[data-lz-set], [data-lz-upsell-add]');
  if (!button || mutationPending || button.disabled) return;
  const isOffer = button.hasAttribute('data-lz-upsell-add');
  const status = isOffer ? dialog.querySelector('[data-lz-upsell-status]') : button.closest('.lz-set').querySelector('.lz-wardrobe__status');
  const originalText = button.textContent;
  mutationPending = true;
  button.disabled = true;
  status.textContent = '';
  button.textContent = labels.adding;
  button.setAttribute('aria-busy', 'true');
  try {
    let handles;
    if (isOffer) {
      const cart = await getCart();
      if (!activeOffer || signature(cart) !== activeOffer.signature) {
        const refreshed = chooseOffer(cart);
        if (!refreshed) closeOffer();
        else {
          paintOffer(refreshed);
          status.textContent = labels.changed;
        }
        return;
      }
      handles = [activeOffer.handle];
      closeOffer(false);
    } else {
      handles = sets.find((set) => set.id === button.dataset.lzSet).handles;
    }
    await addProducts(handles);
  } catch (error) {
    if (isOffer && !dialog.open) dialog.showModal();
    status.textContent = error.message || labels.error;
  } finally {
    mutationPending = false;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = isOffer && activeOffer ? (activeOffer.size === 2 ? labels.add_duo : labels.add_trio) : originalText;
  }
});

renderCards();
